import { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	InputAdornment,
	Link,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import {
	Panel,
	compactInputSx,
	outlinedButtonSx,
	primaryButtonSx,
} from "../sharedStyles";
import { downloadTable } from "../exportTable";

const DETAIL_TABS = [
	{ key: "dispatch", label: "Dispatch" },
	{ key: "rtv", label: "RTV" },
	{ key: "sec_sale", label: "Sec Sale" },
	{ key: "barcode", label: "Barcode" },
	{ key: "cus_soh", label: "CUS-SOH" },
	{ key: "dis_git", label: "DIS-GIT" },
	{ key: "rtv_git", label: "RTV-GIT" },
	{ key: "shortage_cn", label: "Shortage CN" },
	{ key: "dispute_cn", label: "Dispute CN" },
];

const TAB_ENDPOINTS = {
	dispatch: "/mbo-lfs-qty-reco/dispatch",
	rtv: "/mbo-lfs-qty-reco/rtv",
	sec_sale: "/mbo-lfs-qty-reco/sec-sale",
	barcode: "/mbo-lfs-qty-reco/detail",
	cus_soh: "/mbo-lfs-qty-reco/detail",
	dis_git: "/mbo-lfs-qty-reco/detail",
	rtv_git: "/mbo-lfs-qty-reco/detail",
	shortage_cn: "/mbo-lfs-qty-reco/detail",
	dispute_cn: "/mbo-lfs-qty-reco/detail",
};

const LIVE_TABS = new Set(Object.keys(TAB_ENDPOINTS));

const DISPATCH_COLUMNS = [
	{ key: "transfer_out_date", label: "Transfer Out", width: 120 },
	{ key: "document_no", label: "Document No" },
	{ key: "document_count", label: "Doc Count", align: "right", width: 110 },
	{ key: "dispatch_qty", label: "Qty", align: "right", width: 120 },
];

const DISPATCH_DOC_COLUMNS = [
	{ key: "document_no", label: "Document No" },
	{ key: "owner_site_name", label: "Owner Site" },
	{ key: "reference_site", label: "Reference Site" },
	{ key: "dispatch_qty", label: "Qty", align: "right", width: 120 },
];

const DISPATCH_LINE_COLUMNS = [
	{ key: "transfer_out_date", label: "Transfer Out", width: 120 },
	{ key: "barcode", label: "Barcode", width: 140 },
	{ key: "division", label: "Division", width: 110 },
	{ key: "brand", label: "Brand", width: 110 },
	{ key: "style", label: "Style", width: 110 },
	{ key: "colour", label: "Color", width: 100 },
	{ key: "palette", label: "Palette", width: 110 },
	{ key: "size", label: "Size", width: 80 },
	{ key: "season", label: "Season", width: 100 },
	{ key: "dispatch_qty", label: "Qty", align: "right", width: 90 },
	{ key: "taxable_amount", label: "Taxable Amount", align: "right", width: 130 },
	{ key: "net_amount", label: "Net Amount", align: "right", width: 120 },
];

const RTV_COLUMNS = [
	{ key: "return_date", label: "Return", width: 120 },
	{ key: "document_no", label: "Document No" },
	{ key: "document_count", label: "Doc Count", align: "right", width: 110 },
	{ key: "returned_quantity", label: "Qty", align: "right", width: 120 },
];

const RTV_DOC_COLUMNS = [
	{ key: "document_no", label: "Document No" },
	{ key: "owner_site_name", label: "Owner Site" },
	{ key: "reference_site", label: "Reference Site" },
	{ key: "returned_quantity", label: "Qty", align: "right", width: 120 },
];

const RTV_LINE_COLUMNS = [
	{ key: "return_date", label: "Return Date", width: 120 },
	{ key: "barcode", label: "Barcode", width: 140 },
	{ key: "division", label: "Division", width: 110 },
	{ key: "brand", label: "Brand", width: 110 },
	{ key: "style", label: "Style", width: 110 },
	{ key: "colour", label: "Color", width: 100 },
	{ key: "palette", label: "Palette", width: 110 },
	{ key: "size", label: "Size", width: 80 },
	{ key: "season", label: "Season", width: 100 },
	{ key: "returned_quantity", label: "Qty", align: "right", width: 90 },
	{ key: "taxable_amount", label: "Taxable Amount", align: "right", width: 130 },
	{ key: "net_amount", label: "Net Amount", align: "right", width: 120 },
];

const SEC_SALE_COLUMNS = [
	{ key: "sale_month", label: "Month", width: 120 },
	{ key: "document_no", label: "Bill No" },
	{ key: "document_count", label: "Bill Count", align: "right", width: 110 },
	{ key: "quantity", label: "Qty", align: "right", width: 120 },
];

const SEC_SALE_DOC_COLUMNS = [
	{ key: "document_no", label: "Bill No", width: 140 },
	{ key: "bill_date", label: "Bill Date", width: 120 },
	{ key: "channel", label: "Channel", width: 100 },
	{ key: "transaction_site", label: "Transaction Site" },
	{ key: "quantity", label: "Qty", align: "right", width: 90 },
	{ key: "gross_amount", label: "Gross Amt", align: "right", width: 120 },
	{ key: "discount_amount", label: "Discount", align: "right", width: 120 },
	{ key: "net_amount", label: "Net Amt", align: "right", width: 120 },
];

const SEC_SALE_LINE_COLUMNS = [
	{ key: "bill_date", label: "Bill Date", width: 120 },
	{ key: "barcode", label: "Barcode", width: 140 },
	{ key: "division", label: "Division", width: 110 },
	{ key: "brand", label: "Brand", width: 110 },
	{ key: "style", label: "Style", width: 110 },
	{ key: "colour", label: "Color", width: 100 },
	{ key: "palette", label: "Palette", width: 110 },
	{ key: "size", label: "Size", width: 80 },
	{ key: "season", label: "Season", width: 100 },
	{ key: "quantity", label: "Qty", align: "right", width: 90 },
	{ key: "gross_amount", label: "Gross Amt", align: "right", width: 120 },
	{ key: "discount_amount", label: "Discount", align: "right", width: 120 },
	{ key: "net_amount", label: "Net Amount", align: "right", width: 120 },
];

const BARCODE_COLUMNS = [
	{ key: "status", label: "Status", width: 132, pin: "left" },
	{ key: "barcode", label: "Barcode", width: 150, pin: "left" },
	{ key: "division", label: "Division", width: 110 },
	{ key: "brand", label: "Brand", width: 110 },
	{ key: "style", label: "Style", width: 110 },
	{ key: "colour", label: "Color", width: 100 },
	{ key: "size", label: "Size", width: 80 },
	{ key: "dis_qty", label: "DIS QTY", align: "right", width: 100 },
	{ key: "rtv_qty", label: "RTV QTY", align: "right", width: 100 },
	{ key: "sec_sale_qty", label: "SEC-SALE QTY", align: "right", width: 120 },
	{ key: "cus_soh", label: "CUS-SOH", align: "right", width: 100 },
	{ key: "dis_git", label: "DIS-GIT", align: "right", width: 100 },
	{ key: "rtv_git", label: "RTV-GIT", align: "right", width: 100 },
	{ key: "dis_git_2", label: "DIS-GIT-2", align: "right", width: 110 },
	{ key: "rtv_git_2", label: "RTV-GIT-2", align: "right", width: 110 },
	{ key: "shortage_cn", label: "SHORTAGE-CN", align: "right", width: 120 },
	{ key: "dispute_cn", label: "DISPUTE-CN", align: "right", width: 120 },
	{ key: "diff", label: "Diff", align: "right", width: 100, pin: "right", highlight: true },
];

const DRILL = {
	dispatch: {
		monthType: "dispatch_month",
		documentType: "dispatch_document",
		labelKey: "transfer_out_date",
		docColumns: DISPATCH_DOC_COLUMNS,
		lineColumns: DISPATCH_LINE_COLUMNS,
	},
	rtv: {
		monthType: "rtv_month",
		documentType: "rtv_document",
		labelKey: "return_date",
		docColumns: RTV_DOC_COLUMNS,
		lineColumns: RTV_LINE_COLUMNS,
	},
	sec_sale: {
		monthType: "sec_sale_month",
		documentType: "sec_sale_document",
		labelKey: "sale_month",
		docColumns: SEC_SALE_DOC_COLUMNS,
		lineColumns: SEC_SALE_LINE_COLUMNS,
	},
};

const TAB_COLUMNS = {
	dispatch: DISPATCH_COLUMNS,
	rtv: RTV_COLUMNS,
	sec_sale: SEC_SALE_COLUMNS,
	barcode: BARCODE_COLUMNS,
	cus_soh: [
		{ key: "stock_date", label: "Date", width: "25%" },
		{ key: "location", label: "Location", width: "25%" },
		{ key: "barcode", label: "Barcode", width: "25%" },
		{ key: "qty", label: "Qty", align: "right", width: "25%" },
	],
	dis_git: [
		{ key: "git_group", label: "Group", width: "16.66%" },
		{ key: "stock_date", label: "Date", width: "16.66%" },
		{ key: "location", label: "Location", width: "16.66%" },
		{ key: "document_no", label: "Document No", width: "16.66%" },
		{ key: "barcode", label: "Barcode", width: "16.66%" },
		{ key: "qty", label: "Qty", align: "right", width: "16.7%" },
	],
	rtv_git: [
		{ key: "stock_date", label: "Date", width: "20%" },
		{ key: "location", label: "Location", width: "20%" },
		{ key: "document_no", label: "Document No", width: "20%" },
		{ key: "barcode", label: "Barcode", width: "20%" },
		{ key: "qty", label: "Qty", align: "right", width: "20%" },
	],
	shortage_cn: [
		{ key: "stock_date", label: "Date", width: "20%" },
		{ key: "location", label: "Location", width: "20%" },
		{ key: "document_no", label: "Document No", width: "20%" },
		{ key: "barcode", label: "Barcode", width: "20%" },
		{ key: "qty", label: "Qty", align: "right", width: "20%" },
	],
	dispute_cn: [
		{ key: "stock_date", label: "Date", width: "20%" },
		{ key: "location", label: "Location", width: "20%" },
		{ key: "document_no", label: "Document No", width: "20%" },
		{ key: "barcode", label: "Barcode", width: "20%" },
		{ key: "qty", label: "Qty", align: "right", width: "20%" },
	],
};

const COLUMNS = [
	{ key: "bill_date", label: "Bill Date" },
	{ key: "bill_no", label: "Bill No" },
	{ key: "site_code", label: "Site" },
	{ key: "store_name", label: "Store" },
	{ key: "barcode", label: "Barcode" },
	{ key: "style", label: "Style" },
	{ key: "color", label: "Color" },
	{ key: "size", label: "Size" },
	{ key: "mbo_qty", label: "MBO Qty", align: "right" },
	{ key: "lfs_qty", label: "LFS Qty", align: "right" },
	{ key: "diff_qty", label: "Diff", align: "right", highlight: true },
	{ key: "status", label: "Status" },
];

const statusSx = {
	Matched: { bgcolor: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" },
	"Not Matched": { bgcolor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
	Mismatch: { bgcolor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
	"Missing in LFS": { bgcolor: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" },
	"Extra in LFS": { bgcolor: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" },
};

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	letterSpacing: 0.25,
	whiteSpace: "nowrap",
	bgcolor: "#fafafa",
	borderBottom: "1px solid #e5e7eb",
	py: 1,
	px: 1.25,
	color: "#374151",
};

const bodyCellSx = {
	whiteSpace: "nowrap",
	py: 1.1,
	px: 1.25,
	fontSize: "0.8125rem",
	borderBottom: "1px solid #f1f5f9",
	color: "#111827",
	overflow: "hidden",
};

const columnWidthSx = (col) =>
	col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : {};

const stickySx = (col, cols, tone) => {
	if (col.pin !== "left" && col.pin !== "right") return {};
	const bgcolor = tone === "header" ? "#fafafa" : tone === "total" ? "#f8fafc" : "#fff";
	const zIndex = tone === "header" ? 6 : 3;
	if (col.pin === "right") {
		return {
			position: "sticky",
			right: 0,
			zIndex,
			bgcolor,
			borderLeft: "1px solid #e5e7eb",
			boxShadow: "-2px 0 6px rgba(15,23,42,0.04)",
		};
	}
	let left = 0;
	for (const item of cols) {
		if (item.key === col.key) break;
		if (item.pin === "left") left += item.width || 0;
	}
	const pinned = cols.filter((item) => item.pin === "left");
	const isLast = pinned[pinned.length - 1]?.key === col.key;
	return {
		position: "sticky",
		left,
		zIndex,
		bgcolor,
		...(isLast
			? { borderRight: "1px solid #e5e7eb", boxShadow: "2px 0 6px rgba(15,23,42,0.04)" }
			: {}),
	};
};

const barcodeDiff = (row) => {
	const n = (key) => Number(row[key]) || 0;
	return (
		n("dis_qty") -
		n("rtv_qty") -
		n("sec_sale_qty") -
		n("cus_soh") -
		n("dis_git") -
		n("rtv_git") -
		n("dis_git_2") -
		n("rtv_git_2") -
		n("shortage_cn") -
		n("dispute_cn")
	);
};

const barcodeStatus = (diff) => (diff === 0 ? "Matched" : "Not Matched");

function compareCell(a, b, numeric) {
	const emptyA = a === null || a === undefined || a === "";
	const emptyB = b === null || b === undefined || b === "";
	if (emptyA && emptyB) return 0;
	if (emptyA) return 1;
	if (emptyB) return -1;
	if (numeric) return (Number(a) || 0) - (Number(b) || 0);
	const left = String(a);
	const right = String(b);
	if (left < right) return -1;
	if (left > right) return 1;
	return 0;
}

const EllipsisCell = memo(function EllipsisCell({ text, align }) {
	const display = text === null || text === undefined || text === "" ? "—" : String(text);
	const content = (
		<Box
			component="span"
			sx={{
				display: "block",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				textAlign: align || "left",
			}}
		>
			{display}
		</Box>
	);

	if (display.length < 24) return content;

	return (
		<Tooltip
			title={display}
			placement="top"
			enterDelay={400}
			componentsProps={{
				tooltip: { sx: { maxWidth: 480, fontSize: "0.75rem", lineHeight: 1.4 } },
			}}
		>
			{content}
		</Tooltip>
	);
});

const detailView = {
	month: null,
	monthKey: "",
	customer: null,
	activeTab: "dispatch",
	search: "",
	rows: [],
	hasRun: false,
	page: 0,
	rowsPerPage: 25,
	drill: null,
	sort: { key: "transfer_out_date", dir: "desc" },
};

const savedDetail = (customer, monthKey) => {
	if (!detailView.hasRun) return null;
	if (customer && detailView.customer !== customer) return null;
	if (monthKey && detailView.monthKey !== monthKey) return null;
	return detailView;
};

function Detail() {
	const [searchParams] = useSearchParams();
	const { setSnackBar, setSnackBarOpen } = GlobalState();
	const linkedCustomer = searchParams.get("customer");
	const linkedMonth = searchParams.get("month");
	const restored = useRef(savedDetail(linkedCustomer, linkedMonth));
	const [month, setMonth] = useState(() => {
		if (restored.current?.month?.isValid?.()) return restored.current.month;
		const parsed = linkedMonth ? dayjs(`${linkedMonth}-01`) : null;
		return parsed?.isValid?.() ? parsed.startOf("month") : dayjs().startOf("month");
	});
	const [customer, setCustomer] = useState(restored.current?.customer || linkedCustomer || null);
	const [customers, setCustomers] = useState([]);
	const [customersLoading, setCustomersLoading] = useState(false);
	const [activeTab, setActiveTab] = useState(restored.current?.activeTab || DETAIL_TABS[0].key);
	const [searchInput, setSearchInput] = useState(restored.current?.search || "");
	const [search, setSearch] = useState((restored.current?.search || "").trim().toLowerCase());
	const [rows, setRows] = useState(restored.current?.rows || []);
	const [loading, setLoading] = useState(false);
	const [hasRun, setHasRun] = useState(Boolean(restored.current));
	const [page, setPage] = useState(restored.current?.page || 0);
	const [rowsPerPage, setRowsPerPage] = useState(restored.current?.rowsPerPage || 25);
	const [drill, setDrill] = useState(restored.current?.drill || null);
	const [sort, setSort] = useState(restored.current?.sort || { key: "transfer_out_date", dir: "desc" });
	const openedFromSummary = useRef(Boolean(restored.current));
	const skipPageReset = useRef(true);
	const loadSeq = useRef(0);

	const clearSearch = () => {
		setSearchInput("");
		setSearch("");
	};

	useEffect(() => {
		const handle = setTimeout(() => {
			const next = searchInput.trim().toLowerCase();
			startTransition(() => setSearch(next));
		}, 200);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({ ...prev, message, severity }));
	};

	const loadDetail = useCallback(
		async (selectedMonth, selectedCustomer, tab) => {
			if (!LIVE_TABS.has(tab)) {
				showSnack("This tab is not connected yet", "info");
				return;
			}
			if (!selectedMonth?.isValid?.()) {
				showSnack("Select a month", "error");
				return;
			}
			if (!selectedCustomer) {
				showSnack("Select a customer", "error");
				return;
			}
			const requestId = ++loadSeq.current;
			setLoading(true);
			setHasRun(true);
			setDrill(null);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/detail", {
					params: {
						type: tab,
						month: selectedMonth.format("YYYY-MM"),
						customer: selectedCustomer,
					},
				});
				if (requestId !== loadSeq.current) return;
				setRows(res?.data?.result?.rows || []);
			} catch (err) {
				if (requestId !== loadSeq.current) return;
				const data = err?.response?.data;
				const message =
					(typeof data === "string" && data.trim()) ||
					data?.message ||
					err?.message ||
					"Failed to load detail";
				setRows([]);
				showSnack(message, "error");
			} finally {
				if (requestId === loadSeq.current) setLoading(false);
			}
		},
		[setSnackBar, setSnackBarOpen]
	);

	const loadDispatchMonth = useCallback(
		async (row) => {
			const drillTab = DRILL[activeTab];
			if (!drillTab || drill || !row?.month || !customer) return;
			const requestId = ++loadSeq.current;
			setLoading(true);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/detail", {
					params: {
						type: drillTab.monthType,
						month: row.month,
						customer,
					},
				});
				if (requestId !== loadSeq.current) return;
				const monthRows = res?.data?.result?.rows || [];
				const monthLabel = row[drillTab.labelKey] || row.month;
				setDrill({
					level: "month",
					monthLabel,
					label: monthLabel,
					month: row.month,
					monthRows,
					rows: monthRows,
				});
				setPage(0);
				setSearchInput("");
						setSearch("");
			} catch (err) {
				if (requestId !== loadSeq.current) return;
				const data = err?.response?.data;
				const message =
					(typeof data === "string" && data.trim()) ||
					data?.message ||
					err?.message ||
					"Failed to load documents";
				showSnack(message, "error");
			} finally {
				if (requestId === loadSeq.current) setLoading(false);
			}
		},
		[activeTab, customer, drill, setSnackBar, setSnackBarOpen]
	);

	const loadDispatchDocument = useCallback(
		async (row) => {
			const drillTab = DRILL[activeTab];
			if (!drillTab || drill?.level !== "month" || !drill?.month || !customer) return;
			if (!row?.document_no) {
				showSnack("This row has no document number", "info");
				return;
			}
			const requestId = ++loadSeq.current;
			setLoading(true);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/detail", {
					params: {
						type: drillTab.documentType,
						month: drill.month,
						customer,
						document: row.document_no,
					},
				});
				if (requestId !== loadSeq.current) return;
				setDrill((current) => ({
					...current,
					level: "document",
					documentNo: row.document_no,
					label: `${current.monthLabel} → ${row.document_no}`,
					rows: res?.data?.result?.rows || [],
				}));
				setPage(0);
				setSearchInput("");
						setSearch("");
			} catch (err) {
				if (requestId !== loadSeq.current) return;
				const data = err?.response?.data;
				const message =
					(typeof data === "string" && data.trim()) ||
					data?.message ||
					err?.message ||
					"Failed to load document";
				showSnack(message, "error");
			} finally {
				if (requestId === loadSeq.current) setLoading(false);
			}
		},
		[activeTab, customer, drill, setSnackBar, setSnackBarOpen]
	);

	useEffect(() => {
		if (loading || !hasRun) return;
		detailView.month = month;
		detailView.monthKey = month?.isValid?.() ? month.format("YYYY-MM") : "";
		detailView.customer = customer;
		detailView.activeTab = activeTab;
		detailView.search = searchInput;
		detailView.rows = rows;
		detailView.hasRun = hasRun;
		detailView.page = page;
		detailView.rowsPerPage = rowsPerPage;
		detailView.drill = drill;
		detailView.sort = sort;
	}, [loading, hasRun, month, customer, activeTab, searchInput, rows, page, rowsPerPage, drill, sort]);

	useEffect(() => {
		if (openedFromSummary.current) return;
		if (!linkedCustomer || !linkedMonth) return;
		const selectedMonth = dayjs(`${linkedMonth}-01`);
		if (!selectedMonth.isValid()) return;
		openedFromSummary.current = true;
		loadDetail(selectedMonth.startOf("month"), linkedCustomer, "dispatch");
	}, [linkedCustomer, linkedMonth, loadDetail]);

	useEffect(() => {
		let active = true;
		(async () => {
			setCustomersLoading(true);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/customers");
				if (active) setCustomers(res?.data?.result?.customers || []);
			} catch {
				if (active) setCustomers([]);
			} finally {
				if (active) setCustomersLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	const customerOptions = useMemo(() => {
		if (!customer || customers.includes(customer)) return customers;
		return [customer, ...customers];
	}, [customer, customers]);

	const activeTabLabel = DETAIL_TABS.find((tab) => tab.key === activeTab)?.label || "Detail";

	const drillTab = DRILL[activeTab];
	const columns =
		drill?.level === "document" && drillTab
			? drillTab.lineColumns
			: drill && drillTab
				? drillTab.docColumns
				: TAB_COLUMNS[activeTab] || COLUMNS;
	const isLiveTab = LIVE_TABS.has(activeTab);

	const indexedRows = useMemo(() => {
		const source = drill && drillTab ? drill.rows : isLiveTab ? rows : [];
		const isBarcode = activeTab === "barcode" && !drill;
		const textKeys = [];
		for (let i = 0; i < columns.length; i += 1) {
			if (columns[i].align !== "right") textKeys.push(columns[i].key);
		}
		return source.map((row) => {
			const diff = isBarcode ? barcodeDiff(row) : row.diff;
			const next = isBarcode ? { ...row, diff, status: barcodeStatus(diff) } : { ...row };
			let q = "";
			for (let i = 0; i < textKeys.length; i += 1) {
				const value = next[textKeys[i]];
				if (value === null || value === undefined || value === "") continue;
				q += String(value).toLowerCase();
				q += " ";
			}
			next._q = q;
			return next;
		});
	}, [activeTab, columns, drill, drillTab, isLiveTab, rows]);

	const view = useMemo(() => {
		const numericKeys = [];
		for (let i = 0; i < columns.length; i += 1) {
			if (columns[i].align === "right") numericKeys.push(columns[i].key);
		}
		const totals = {};
		const seen = {};
		for (let i = 0; i < numericKeys.length; i += 1) {
			totals[numericKeys[i]] = 0;
			seen[numericKeys[i]] = false;
		}
		const matchedRows = [];
		let matched = 0;
		let notMatched = 0;
		for (let i = 0; i < indexedRows.length; i += 1) {
			const row = indexedRows[i];
			if (search && row._q.indexOf(search) === -1) continue;
			matchedRows.push(row);
			if (row.status === "Matched") matched += 1;
			else if (row.status === "Not Matched") notMatched += 1;
			for (let k = 0; k < numericKeys.length; k += 1) {
				const key = numericKeys[k];
				const value = row[key];
				if (value !== null && value !== undefined && value !== "") seen[key] = true;
				totals[key] += Number(value) || 0;
			}
		}
		return { rows: matchedRows, totals, seen, matched, notMatched };
	}, [columns, indexedRows, search]);

	const filteredRows = view.rows;
	const barcodeCounts =
		activeTab === "barcode" && !drill ? { matched: view.matched, notMatched: view.notMatched } : null;

	useEffect(() => {
		if (skipPageReset.current) {
			skipPageReset.current = false;
			return;
		}
		setPage(0);
	}, [search, rows, activeTab]);

	useEffect(() => {
		if (drill?.level === "document") {
			const dateKey =
				activeTab === "sec_sale" ? "bill_date" : activeTab === "rtv" ? "return_date" : "transfer_out_date";
			setSort({ key: dateKey, dir: "asc" });
		}
		else if (drill?.level === "month") setSort({ key: "document_no", dir: "asc" });
		else if (activeTab === "dispatch") setSort({ key: "transfer_out_date", dir: "desc" });
		else if (activeTab === "rtv") setSort({ key: "return_date", dir: "desc" });
		else if (activeTab === "sec_sale") setSort({ key: "sale_month", dir: "desc" });
		else if (activeTab === "barcode") setSort({ key: "barcode", dir: "asc" });
		else if (activeTab === "dis_git") setSort({ key: "git_group", dir: "asc" });
		else if (activeTab === "cus_soh" || activeTab === "rtv_git" || activeTab === "shortage_cn" || activeTab === "dispute_cn")
			setSort({ key: "stock_date", dir: "desc" });
		else setSort({ key: "bill_date", dir: "desc" });
	}, [activeTab, drill?.level]);

	const sortedRows = useMemo(() => {
		const col = columns.find((item) => item.key === sort.key);
		const numeric = col?.align === "right";
		const dir = sort.dir === "desc" ? -1 : 1;
		const dateSort =
			sort.key === "transfer_out_date" || sort.key === "return_date" || sort.key === "sale_month";
		const list = filteredRows.slice();
		list.sort((left, right) => {
			const key = dateSort && left.month && right.month ? "month" : sort.key;
			return compareCell(left[key], right[key], numeric && key === sort.key) * dir;
		});
		return list;
	}, [columns, filteredRows, sort]);

	const pagedRows = useMemo(() => {
		const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage) || 1);
		const safePage = Math.min(page, pageCount - 1);
		const start = safePage * rowsPerPage;
		return sortedRows.slice(start, start + rowsPerPage);
	}, [sortedRows, page, rowsPerPage]);

	const qtyTotals = view.totals;
	const totalsSeen = view.seen;

	const cellSticky = useMemo(() => {
		const header = {};
		const body = {};
		const total = {};
		columns.forEach((col) => {
			header[col.key] = stickySx(col, columns, "header");
			body[col.key] = stickySx(col, columns, "body");
			total[col.key] = stickySx(col, columns, "total");
		});
		return { header, body, total };
	}, [columns]);

	return (
		<Box>
			<Panel sx={{ px: 2, py: 1.5, mb: 1.5 }}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack
						direction={{ xs: "column", lg: "row" }}
						spacing={1.25}
						alignItems={{ xs: "stretch", lg: "center" }}
						justifyContent="space-between"
					>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={1.25}
							alignItems={{ xs: "stretch", sm: "center" }}
							flexWrap="wrap"
							useFlexGap
						>
							<DatePicker
								label="Month"
								views={["year", "month"]}
								openTo="month"
								value={month}
								onChange={(value) => setMonth(value ? value.startOf("month") : value)}
								format="MMM YYYY"
								slotProps={{
									textField: {
										size: "small",
										sx: {
											width: { xs: "100%", sm: 160 },
											flexShrink: 0,
											...compactInputSx,
										},
									},
								}}
							/>
							<Autocomplete
								size="small"
								options={customerOptions}
								value={customer}
								onChange={(_, value) => setCustomer(value)}
								loading={customersLoading}
								sx={{ width: { xs: "100%", sm: 280 }, flexShrink: 0 }}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Customer"
										placeholder="All customers"
										sx={compactInputSx}
									/>
								)}
							/>
						</Stack>
						<Stack direction="row" spacing={1}>
							<Button
								variant="contained"
								size="small"
								disabled={loading}
								onClick={() => loadDetail(month, customer, activeTab)}
								startIcon={
									loading ? <CircularProgress size={14} color="inherit" /> : <RefreshOutlinedIcon />
								}
								sx={{ ...primaryButtonSx, height: 36 }}
							>
								{loading ? "Running..." : "Run"}
							</Button>
							<Button
								variant="outlined"
								size="small"
								disabled={loading || sortedRows.length === 0}
								startIcon={<DownloadOutlinedIcon />}
								onClick={() => {
									const monthName = month?.isValid?.() ? month.format("YYYY-MM") : "detail";
									const customerName = String(customer || "customer").replace(/[^\w]+/g, "_");
									downloadTable(
										`mbo_lfs_${activeTab}_${customerName}_${monthName}.xlsx`,
										columns,
										sortedRows
									);
								}}
								sx={{ ...outlinedButtonSx, height: 36 }}
							>
								Export
							</Button>
						</Stack>
					</Stack>
				</LocalizationProvider>
			</Panel>

			<Panel sx={{ overflow: "hidden" }}>
				<Tabs
					value={activeTab}
					onChange={(_, value) => {
						setActiveTab(value);
						setRows([]);
						setDrill(null);
						setSearchInput("");
						setSearch("");
						setPage(0);
						setHasRun(false);
						if (LIVE_TABS.has(value) && month?.isValid?.() && customer) {
							loadDetail(month, customer, value);
						}
					}}
					variant="scrollable"
					scrollButtons="auto"
					sx={{
						minHeight: 40,
						px: 1,
						borderBottom: "1px solid #eef0f3",
						"& .MuiTab-root": {
							minHeight: 40,
							px: 1.5,
							textTransform: "none",
							fontWeight: 600,
							fontSize: "0.8125rem",
							color: "#6b7280",
						},
						"& .Mui-selected": { color: "#111827" },
						"& .MuiTabs-indicator": { bgcolor: "#111827", height: 2 },
					}}
				>
					{DETAIL_TABS.map((tab) => (
						<Tab key={tab.key} value={tab.key} label={tab.label} />
					))}
				</Tabs>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef0f3" }}
				>
					<Stack direction="row" spacing={1} alignItems="center">
						{drill && drillTab && (
							<Button
								size="small"
								startIcon={<ArrowBackOutlinedIcon />}
								onClick={() => {
									if (drill?.level === "document") {
										setDrill((current) => ({
											...current,
											level: "month",
											documentNo: null,
											label: current.monthLabel,
											rows: current.monthRows || [],
										}));
									} else {
										setDrill(null);
									}
									setPage(0);
									setSearchInput("");
						setSearch("");
								}}
								sx={{ ...outlinedButtonSx, height: 28, textTransform: "none" }}
							>
								Back
							</Button>
						)}
						<Stack direction="row" spacing={0.75} alignItems="center">
							{drill && drillTab ? (
								<Link
									component="button"
									type="button"
									underline="hover"
									onClick={() => {
										setDrill(null);
										setPage(0);
										setSearchInput("");
						setSearch("");
									}}
									sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}
								>
									{activeTabLabel}
								</Link>
							) : (
								<Typography variant="subtitle2" fontWeight={700} color="#111827">
									{activeTabLabel}
								</Typography>
							)}
							{drill && drillTab && (
								<>
									<Typography variant="subtitle2" color="#9ca3af">
										→
									</Typography>
									{drill.level === "document" ? (
										<Link
											component="button"
											type="button"
											underline="hover"
											onClick={() => {
												setDrill((current) => ({
													...current,
													level: "month",
													documentNo: null,
													label: current.monthLabel,
													rows: current.monthRows || [],
												}));
												setPage(0);
												setSearchInput("");
						setSearch("");
											}}
											sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}
										>
											{drill.monthLabel}
										</Link>
									) : (
										<Typography variant="subtitle2" fontWeight={700} color="#111827">
											{drill.monthLabel}
										</Typography>
									)}
								</>
							)}
							{drill?.level === "document" && (
								<>
									<Typography variant="subtitle2" color="#9ca3af">
										→
									</Typography>
									<Typography variant="subtitle2" fontWeight={700} color="#111827">
										{drill.documentNo}
									</Typography>
								</>
							)}
						</Stack>
					</Stack>
					<Stack direction="row" spacing={1} alignItems="center">
						<TextField
							size="small"
							placeholder="Search"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							sx={{ width: { xs: 160, sm: 240 }, ...compactInputSx }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
									</InputAdornment>
								),
								endAdornment: searchInput ? (
									<InputAdornment position="end">
										<IconButton size="small" aria-label="Clear search" onClick={clearSearch}>
											<CloseOutlinedIcon sx={{ fontSize: 16 }} />
										</IconButton>
									</InputAdornment>
								) : null,
							}}
						/>
						<Chip
							label={`${filteredRows.length} rows`}
							size="small"
							sx={{
								height: 24,
								fontWeight: 600,
								fontSize: "0.7rem",
								bgcolor: "#f3f4f6",
								color: "#374151",
							}}
						/>
						{barcodeCounts && hasRun && (
							<>
								<Chip
									label={`Matched ${barcodeCounts.matched.toLocaleString("en-IN")}`}
									size="small"
									sx={{
										height: 24,
										fontWeight: 600,
										fontSize: "0.7rem",
										bgcolor: "#f0fdf4",
										color: "#15803d",
										border: "1px solid #bbf7d0",
									}}
								/>
								<Chip
									label={`Not Matched ${barcodeCounts.notMatched.toLocaleString("en-IN")}`}
									size="small"
									sx={{
										height: 24,
										fontWeight: 600,
										fontSize: "0.7rem",
										bgcolor: "#fef2f2",
										color: "#b91c1c",
										border: "1px solid #fecaca",
									}}
								/>
							</>
						)}
					</Stack>
				</Stack>

				<TableContainer sx={{ maxHeight: "calc(100vh - 280px)", overflow: "auto" }}>
					<Table
						stickyHeader
						size="small"
						sx={{
							tableLayout: "fixed",
							width: activeTab === "barcode" && !drill ? 1980 : "100%",
						}}
					>
						<TableHead>
							<TableRow>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align || "left"}
										sortDirection={sort.key === col.key ? sort.dir : false}
										sx={{ ...headerCellSx, ...columnWidthSx(col), ...cellSticky.header[col.key] }}
									>
										<TableSortLabel
											active={sort.key === col.key}
											direction={sort.key === col.key ? sort.dir : "asc"}
											onClick={() => {
												setSort((current) =>
													current.key === col.key
														? { key: col.key, dir: current.dir === "asc" ? "desc" : "asc" }
														: { key: col.key, dir: col.align === "right" ? "desc" : "asc" }
												);
												setPage(0);
											}}
										>
											{col.label}
										</TableSortLabel>
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={columns.length} align="center" sx={{ py: 8, borderBottom: 0 }}>
										<Stack alignItems="center" spacing={1.25}>
											<CircularProgress size={28} />
											<Typography color="text.secondary" variant="body2">
												Loading...
											</Typography>
										</Stack>
									</TableCell>
								</TableRow>
							) : filteredRows.length > 0 && (
								<TableRow>
									{columns.map((col, index) => (
										<TableCell
											key={col.key}
											align={index === 0 ? "left" : col.align || "left"}
											sx={{
												...bodyCellSx,
												...columnWidthSx(col),
												fontWeight: 700,
												bgcolor: "#f8fafc",
												fontVariantNumeric: col.align === "right" ? "tabular-nums" : undefined,
												...cellSticky.total[col.key],
											}}
										>
											{index === 0
												? "Total"
												: col.align === "right" && totalsSeen[col.key]
													? Number(qtyTotals[col.key] || 0).toLocaleString("en-IN")
													: ""}
										</TableCell>
									))}
								</TableRow>
							)}
							{!loading && pagedRows.map((row) => (
								<TableRow
									key={row.id}
									hover
									onClick={() =>
										drill?.level === "month" ? loadDispatchDocument(row) : loadDispatchMonth(row)
									}
									sx={{
										cursor: drillTab && drill?.level !== "document" ? "pointer" : "default",
										"&:hover td": { bgcolor: "#f9fafb" },
										"&:last-child td": { borderBottom: 0 },
									}}
								>
									{columns.map((col) => {
										const value = row[col.key];
										if (col.key === "status" || col.key === "git_group") {
											const groupSx =
												value === "RTPL-DIS"
													? { bgcolor: "#f3f4f6", color: "#111827", border: "1px solid #e5e7eb" }
													: value === "DIS-RET"
														? { bgcolor: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }
														: {};
											return (
												<TableCell
													key={col.key}
													sx={{ ...bodyCellSx, ...columnWidthSx(col), ...cellSticky.body[col.key] }}
												>
													<Chip
														size="small"
														label={value || "—"}
														sx={{
															height: 22,
															fontSize: "0.7rem",
															fontWeight: 600,
															...(col.key === "git_group" ? groupSx : statusSx[value] || {}),
														}}
													/>
												</TableCell>
											);
										}
										if (col.highlight) {
											return (
												<TableCell
													key={col.key}
													align="right"
													sx={{
														...bodyCellSx,
														...columnWidthSx(col),
														...cellSticky.body[col.key],
														fontWeight: 700,
														color: Number(value) === 0 ? "#15803d" : "#b91c1c",
														fontVariantNumeric: "tabular-nums",
													}}
												>
													{value === null || value === undefined || value === ""
														? ""
														: Number(value).toLocaleString("en-IN")}
												</TableCell>
											);
										}
										return (
											<TableCell
												key={col.key}
												align={col.align || "left"}
												sx={{
													...bodyCellSx,
													...columnWidthSx(col),
													...cellSticky.body[col.key],
													fontVariantNumeric: col.align === "right" ? "tabular-nums" : undefined,
												}}
											>
												<EllipsisCell
													align={col.align}
													text={
														value === null || value === undefined || value === ""
															? ""
															: col.align === "right"
																? Number(value).toLocaleString("en-IN")
																: value
													}
												/>
											</TableCell>
										);
									})}
								</TableRow>
							))}
							{!loading && filteredRows.length === 0 && (
								<TableRow>
									<TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
										<Typography color="text.secondary">
											{isLiveTab && !hasRun
												? "Select a month and customer, then click Run."
												: "No rows found"}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={filteredRows.length}
					page={filteredRows.length === 0 ? 0 : Math.min(page, Math.ceil(filteredRows.length / rowsPerPage) - 1)}
					onPageChange={(_, nextPage) => setPage(nextPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[25, 50, 100]}
				/>
			</Panel>
		</Box>
	);
}

export default Detail;
